import { Dialog, Icon, InputText, Tooltip } from "@uigovpe/components";
import { useEffect, useId, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { ProfileFormData } from "../../../types";
import { useImageSource } from "@/hooks/images/useImageSource";

type ImageField = "cover" | "logo";

export default function VisualIdentityImageUpload({
  type,
}: {
  type: ImageField;
}) {
  const {
    clearErrors,
    getValues,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ProfileFormData>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const tooltipId = `ai-suggestion-${useId().replace(/:/g, "")}`;
  const isCover = type === "cover";
  const fileKey = isCover ? "coverImage" : "logoImage";
  const existingKey = isCover ? "existingCoverImage" : "existingLogoImage";
  const altKey = isCover ? "coverImageAlt" : "logoImageAlt";
  const file = watch(fileKey)?.[0];
  const existing = watch(existingKey);
  const altText = watch(altKey);
  const [localImageUrl, setLocalImageUrl] = useState("");
  const remoteImage = useImageSource(existing?.url);
  const preview = file ? localImageUrl : remoteImage.imageSource;
  const hasImage = Boolean(preview);

  useEffect(() => {
    if (!file) {
      setLocalImageUrl("");
      return;
    }

    const reader = new FileReader();

    reader.addEventListener("load", () => {
      setLocalImageUrl(typeof reader.result === "string" ? reader.result : "");
    });
    reader.readAsDataURL(file);

    return () => reader.abort();
  }, [file]);

  const remove = () => {
    setValue(fileKey, null, { shouldDirty: true, shouldValidate: true });

    if (file) {
      setValue(altKey, existing?.altText ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue(existingKey, null, { shouldDirty: true, shouldValidate: true });
      setValue(altKey, "", { shouldDirty: true, shouldValidate: true });
    }

    clearErrors([fileKey, existingKey, altKey]);
    setIsPreviewOpen(false);
  };

  const fieldLabel = isCover
    ? "Imagem de capa do Perfil (opcional)"
    : "Logo do negócio (opcional)";
  const altPlaceholder = isCover
    ? "Descreva o conteúdo da imagem de capa"
    : "Descreva os elementos visuais da logomarca";

  return (
    <div className="flex! min-w-0! flex-col! gap-2!">
      <span className="text-sm! font-medium! text-zinc-950!">{fieldLabel}</span>
      <label
        className={`group relative! flex! h-40! cursor-pointer! flex-col! items-center! justify-center! overflow-hidden! rounded-sm! bg-white/40! text-center! transition! hover:bg-violet-ultralight/40! ${
          hasImage
            ? "border! border-zinc-200!"
            : "border! border-dashed! border-violet-light! px-6! py-5!"
        }`}
      >
        <input
          key={
            file
              ? `${file.name}-${file.lastModified}`
              : existing
                ? `remote-${existing.id}`
                : "empty"
          }
          type="file"
          accept="image/png,image/jpeg"
          className="hidden!"
          {...register(fileKey, {
            validate: (files) =>
              !files?.[0] ||
              files[0].size <= 5 * 1024 * 1024 ||
              "A imagem deve ter no máximo 5 MB",
          })}
        />

        {preview ? (
          <img
            src={preview}
            alt={
              altText ||
              (isCover
                ? "Pré-visualização da imagem de capa"
                : "Pré-visualização da logomarca")
            }
            className={`absolute! inset-0! h-full! w-full! ${isCover ? "object-cover!" : "object-contain!"}`}
          />
        ) : (
          <>
            <span className="mb-3! flex! size-10! items-center! justify-center! rounded-full! bg-zinc-200! text-primary!">
              <Icon icon="image" outline className="text-[22px]!" />
            </span>
            <span className="text-sm! font-semibold! text-zinc-950!">
              {isCover
                ? "Clique para adicionar a imagem de capa do seu perfil"
                : "Clique para adicionar a logomarca do seu negócio"}
            </span>
            <span className="mt-1! text-sm! text-zinc-600!">
              {isCover
                ? "Formato horizontal PNG ou JPG, até 5 MB."
                : "Formato paisagem recomendado. PNG ou JPG, até 5 MB."}
            </span>
          </>
        )}

        {hasImage && (
          <span className="absolute! inset-0! z-10! flex! items-center! justify-center! gap-4! bg-zinc-950/55! opacity-0! transition! group-hover:opacity-100! group-focus-within:opacity-100!">
            <button
              type="button"
              className="flex! size-10! items-center! justify-center! rounded-sm! border-0! bg-white! p-0! text-primary! shadow-sm! transition! hover:bg-violet-ultralight!"
              aria-label={`Visualizar ${fieldLabel.toLowerCase()}`}
              title="Visualizar"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsPreviewOpen(true);
              }}
            >
              <Icon
                icon="visibility"
                outline
                className="text-[20px]! text-primary!"
              />
            </button>
            <button
              type="button"
              className="flex! size-10! items-center! justify-center! rounded-sm! border-0! bg-white! p-0! text-primary! shadow-sm! transition! hover:bg-violet-ultralight!"
              aria-label={`Excluir ${fieldLabel.toLowerCase()}`}
              title="Excluir"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                remove();
              }}
            >
              <Icon
                icon="delete"
                outline
                className="text-[20px]! text-primary!"
              />
            </button>
          </span>
        )}
      </label>

      {errors[fileKey]?.message && (
        <small className="text-xs! text-red-700!">
          {String(errors[fileKey]?.message)}
        </small>
      )}

      {hasImage && (
        <>
          <div className="mt-2! flex! items-center! justify-between! gap-3!">
            <span className="text-sm! font-medium! text-zinc-950!">
              Descrição da imagem para acessibilidade
            </span>
            <span id={tooltipId} className="shrink-0!">
              <button
                type="button"
                disabled
                className="flex! cursor-not-allowed! items-center! gap-1! rounded-full! border-0! bg-violet-ultralight! px-3! py-1! text-xs! font-medium! text-primary! opacity-60!"
              >
                <Icon
                  icon="wand_stars"
                  className="text-[16px]! text-primary!"
                />
                Sugestão por IA
              </button>
            </span>
          </div>
          <Tooltip
            target={`#${tooltipId}`}
            theme="light"
            position="top"
            content={
              <span>Esta funcionalidade ainda não foi implementada.</span>
            }
          />
          <InputText
            placeholder={altPlaceholder}
            supportText={errors[altKey]?.message as string | undefined}
            invalid={Boolean(errors[altKey])}
            className="w-full!"
            {...register(altKey, {
              validate: (value) => {
                const hasCurrentImage = Boolean(
                  getValues(fileKey)?.[0] || getValues(existingKey),
                );

                return (
                  !hasCurrentImage ||
                  Boolean(value.trim()) ||
                  "Descreva a imagem para acessibilidade"
                );
              },
            })}
          />
          <p className="m-0! text-xs! text-zinc-700!">
            <strong>Como descrever um alt-text:</strong> Informe o tipo de
            imagem, o elemento principal, onde a cena acontece e o que está
            acontecendo. Use frases curtas e verbos no presente.
          </p>
        </>
      )}

      <Dialog
        visible={isPreviewOpen}
        onHide={() => setIsPreviewOpen(false)}
        header={file?.name ?? fieldLabel}
        className="w-[min(92vw,720px)]!"
        draggable={false}
      >
        {preview && (
          <div className="relative! h-[min(70vh,520px)]! w-full! overflow-hidden! rounded-sm! bg-zinc-100!">
            <img
              src={preview}
              alt={altText || fieldLabel}
              className="absolute! inset-0! h-full! w-full! object-contain!"
            />
          </div>
        )}
      </Dialog>
    </div>
  );
}
